import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'

import HomeAction from '../../../../home/actions/HomeAction'
import { getReferencialData, reloadJobSync } from '../../../util/ReferencialUtils'
import {
    PATH_REFERENCIAL,
    PATH_REFERENCIAL_TAXON,
    PATH_REFERENCIAL_TAXON_NEW,
} from '../../../../home/constants/RouteConstants'
import TaxonAction from '../actions/TaxonAction'
import ReferencialAction from '../../../action/ReferencialAction'
import useSandreList from 'utils/customHook/useSandreList'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import { push } from '@lagunovsky/redux-react-router'
import { getDataWithHeaders } from 'utils/ExportDataUtil'
import {
    nbPerPageLabel,
    REFERENCIAL_TYPE_NAME,
    REFERENTIAL_JOBSYNC_ID,
    SANDRE,
} from 'referencial/constants/ReferencialConstants'
import { getDate } from 'utils/DateUtil'
import { hasValue } from 'utils/NumberUtil'
import { searchAllCharacters } from 'utils/StringUtil'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import useSessionStorage from 'utils/customHook/useSessionStorage'
import ImportFileContent from 'import/components/content/ImportFileContent'
import ProgressCard from 'components/card/ProgressCard'
import { getStatusInformation } from 'referencial/util/StatusUtils'
import { keys, orderBy } from 'lodash'
import useListIndexed from 'utils/customHook/useListIndexed'
import JobSyncStatus from '../../JobSyncStatus'
import { CardTitle } from 'components/card/NewCard'
import { CardTable } from 'components/datatable/NewTable'

const headers = ['code', 'theme', 'level', 'latinName', 'status']
const headersCsv = [
    'Code Taxon SANDRE*',
    'Nom latin de l\'appellation du taxon*',
    'Statut de l\'appellation du taxon',
    'Date de création de l\'appellation du taxon',
    'Date de la dernière mise-à-jour de l\'appellation du taxon',
    'Rédacteur de la fiche de l\'appellation du taxon',
    'Références bibliographiques de l\'appellation du taxon',
    'Commentaires sur l\'appellation du taxon',
    'Auteur de l\'appellation du taxon',
    'Code de l\'appellation du taxon (Faire le lien avec le champ "Elément de")',
    'Code du Niveau',
    'Niveau taxonomique',
    'Code du thème*',
    'Libellé du thème des taxons',
    'Code OMNIDIA (teraomnidia)',
    'Synonyme OMNIDIA (pas visible sur l\'écran du référentiel d\'un taxon)',
    'TAXREF (champ "refmuseum")',
    'PHYTOBS (codemacrophytes)',
]

const TaxonsApp = () => {
    const {
        taxons,
        sandreCodes,
        referencialStatus,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        referencialStatus: store.ReferencialReducer.referencialStatus,
    }), shallowEqual)

    const indexedTaxons = useListIndexed(taxons, 'code')

    const dispatch = useDispatch()

    const [filter, setFilter] = useSessionStorage(`referencial_${REFERENCIAL_TYPE_NAME.taxon}`, {})
    const [loading, setLoading] = useState(true)
    const [filterTmp, setFilterTmp] = useState(filter)
    const [fileContent, setFileContent] = useState('')
    const [jobSync, setJobSync] = useState(undefined)

    useEffect(() => {
        reloadJobSync(REFERENTIAL_JOBSYNC_ID.taxon, setJobSync)
    }, [])

    useEffect(() => {
        if (jobSync && jobSync.executions[0]) {
            if (!jobSync.executions[0].statusCode) {
                setTimeout(() => {
                    reloadJobSync(REFERENTIAL_JOBSYNC_ID.taxon, setJobSync)
                }, 5000)
            }
        }
    }, [jobSync])

    const sandreThemes = useSandreList(SANDRE.TAXONS_THEME)
    const sandreLevels = useSandreList(SANDRE.TAXONS_NIVEAUX)

    useEffect(() => {
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
    }, [dispatch, sandreCodes])

    useEffect(() => {
        if (!taxons.length) {
            dispatch(TaxonAction.fetchTaxons()).then(() => {
                setLoading(false)
            })
        } else {
            setLoading(false)
        }

        if (!referencialStatus.length) {
            dispatch(ReferencialAction.fetchReferencialStatus())
        }
        dispatch(HomeAction.setHelpLink('', ''))
    }, [])

    useTitle(() => [{
        title: i18n.referencials,
        href: PATH_REFERENCIAL,
    }, {
        title: i18n.taxons,
        href: PATH_REFERENCIAL_TAXON,
    }], [])

    const taxonsByStatus = useMemo(() => hasValue(filter.status) ? taxons.filter(h => parseInt(h.status) === filter.status) : taxons, [filter.status, taxons])
    const taxonsByTheme = useMemo(() => hasValue(filter.theme) ? taxonsByStatus.filter(h => parseInt(h.theme) === filter.theme) : taxonsByStatus, [filter.theme, taxonsByStatus])
    const referencialData = useMemo(() => getReferencialData(taxonsByTheme), [taxonsByTheme])
    const data = useMemo(() => {
        return referencialData
            .filter((obj) => searchAllCharacters(headers.map((key) => obj[key]).join())
                .includes(searchAllCharacters(filter.searchValue || '')))
    }, [referencialData, filter.searchValue])

    const colorData = useMemo(() => data.map(t => ({
        latinName: t.latinName,
        theme: sandreThemes.find(s => s.code === t.theme)?.name,
        level: sandreLevels.find(s => s.code === t.level)?.name,
        code: t.code,
        active: t.active,
        status: t.status,
    })).map(t => {
        const color = t.active === '1' ? undefined : '#c5c5c5'
        const obj = Object.keys(t).reduce((a, key) => ({
            ...a,
            [key]: {
                value: t[key],
            },
        }), {})
        return {
            ...obj,
            id: t.code,
            colorLine: color,
        }
    }).sort((a, b) => parseInt(a.code.value) - parseInt(b.code.value)), [data, sandreLevels, sandreThemes])

    const onImportFile = (e) => {
        const reader = new FileReader()
        const file = e.target.files[0]
        reader.onload = upload => setFileContent(upload.target.result)
        reader.readAsDataURL(file)
    }

    const getFileContent = (title, subtitle, extension) => (
        <ImportFileContent
            title={title}
            subtitle={subtitle}
            extension={extension}
            headersCsv={headersCsv}
            onImportFile={f => onImportFile(f)}
        />
    )

    const exportedData = useMemo(() => {
        const subTaxons = orderBy(taxons, 'code').slice(0, 100000)
        const formattedData = subTaxons.map(taxon => ({
            code: taxon.code,
            theme: sandreThemes.find(s => s.code === taxon.theme)?.name,
            level: sandreLevels.find(s => s.code === taxon.level)?.name,
            latinName: taxon.latinName,
            status: taxon.status ? getStatusInformation(taxon.status.toString()) : '',
            creationDate: getDate(taxon.creationDate),
            elementOf: indexedTaxons[taxon.elementOf]?.labelWithCode,
            author: taxon.author,
            writter: taxon.writter,
            macrophyteCode: taxon.macrophyteCode,
            omnidiaCode: taxon.omnidiaCode,
            teraOmnidia: taxon.teraOmnidia,
            omnidiaSynonym: taxon.omnidiaSynonym,
            spomnidia: taxon.spomnidia,
            naturalHistoryMuseum: taxon.refmuseum,
            aspe: taxon.aspe,
            basionym: taxon.basionym,
            synonyms: taxon.synonyms,
        }))
        return formattedData
    }, [indexedTaxons, sandreLevels, sandreThemes, taxons])

    useActions(() => {
        return {
            new: () => dispatch(push(PATH_REFERENCIAL_TAXON_NEW)),
            referencialActions: {
                actions: {
                    export: {
                        data: getDataWithHeaders(exportedData, keys(exportedData[0])),
                        titleFile: i18n.taxonList,
                    },
                    sync: {
                        updateAction: options => dispatch(ReferencialAction.launchSyncJob(REFERENTIAL_JOBSYNC_ID.taxon, options, () => reloadJobSync(REFERENTIAL_JOBSYNC_ID.taxon, setJobSync))),
                        withInsertAllOption: true,
                        onImportXml: () => dispatch(TaxonAction.fetchImportTaxonXML(fileContent)),
                        onImportCsv: () => dispatch(TaxonAction.fetchImportTaxonCSV(fileContent)),
                        xmlContent: getFileContent(i18n.xmlSyncChoice, i18n.importXmlFileToZanderReference, '.xml'),
                        csvContent: getFileContent(i18n.csvSyncChoice, i18n.importCsvZanderReference, '.csv'),
                        fileContentIsValid: !!fileContent,
                    },
                },
            },
        }
    }, [exportedData, fileContent])

    return (
        <Stack margin={1} spacing={1}>
            <Card>
                <CardTitle title={i18n.criterias} color='bannerColor'/>
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={2}>
                            <Input
                                title={i18n.search}
                                value={filterTmp.searchValue}
                                onChange={v => setFilterTmp({ ...filterTmp, searchValue: v })}
                                onEnterKeyPress={() => setFilter({ ...filterTmp })}
                                data-cy='taxonSearch'
                            />
                        </Grid>
                        <Grid size={2}>
                            <Select
                                options={referencialStatus}
                                label={i18n.status}
                                onChange={v => setFilterTmp({ ...filterTmp, status: v })}
                                value={filterTmp.status}
                                nullLabel='&nbsp;'
                                data-cy='taxonStatus'
                            />
                        </Grid>
                        <Grid size={2}>
                            <Select
                                options={sandreThemes}
                                label={i18n.theme}
                                onChange={v => setFilterTmp({ ...filterTmp, theme: v })}
                                value={filterTmp.theme}
                                nullLabel='&nbsp;'
                                data-cy='taxonTheme'
                            />
                        </Grid>
                        <Grid size={5}>
                            <JobSyncStatus jobSync={jobSync}/>
                        </Grid>
                        <Grid size={1}>
                            <Button
                                className='right'
                                onClick={() => setFilter({ ...filterTmp })}
                                variant='contained'
                                color='primary'
                                data-cy='taxonSearchButton'
                            >
                                {i18n.search}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {loading && <ProgressCard indeterminate={true} /> || (
                <CardTable
                    data-cy='taxonsTable'
                    color='bannerColor'
                    title={i18n.taxonList}
                    rowsPerPageOptions={nbPerPageLabel}
                    rows={colorData}
                    headers={headers}
                    onClickRow={taxon => dispatch(push(`/referencial/taxon/${taxon.id}/dashboard`))}
                />
            )}
        </Stack>
    )
}

export default TaxonsApp
